import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Package, PackageSearch, Truck } from "lucide-react";
import { api, inr } from "@/lib/api";
import Reveal from "@/components/Reveal";
import Particles, { GradientMesh } from "@/components/Particles";

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get("order") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (id) => {
    if (!id.trim()) return;
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const o = await api.getOrder(id.trim());
      setOrder(o);
    } catch (e) {
      setError(e.response?.data?.detail || "Order not found. Check the ID and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const pre = params.get("order");
    if (pre) lookup(pre);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmt = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

  return (
    <div className="relative min-h-screen overflow-hidden px-4 pb-24 pt-32 sm:px-6" data-testid="track-page">
      <GradientMesh />
      <Particles count={10} />
      <div className="relative mx-auto max-w-2xl">
        <Reveal className="text-center">
          <p className="eyebrow">Concierge</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight sm:text-4xl">
            Where is my <span className="gold-text italic">ritual?</span>
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--mn-text2)" }}>
            Enter the order ID from your confirmation (starts with MN).
          </p>
        </Reveal>

        <form
          onSubmit={(e) => { e.preventDefault(); lookup(orderId); }}
          className="mx-auto mt-8 flex max-w-md gap-2"
          data-testid="track-form"
        >
          <div className="glass flex flex-1 items-center gap-2 rounded-full px-5">
            <PackageSearch size={16} style={{ color: "var(--mn-gold)" }} />
            <input
              data-testid="track-input"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="e.g. MND1FE7AA6"
              className="w-full bg-transparent py-3.5 font-mono text-sm uppercase tracking-wider outline-none placeholder:normal-case placeholder:tracking-normal placeholder:text-[var(--mn-text2)]"
            />
          </div>
          <button data-testid="track-submit" type="submit" disabled={loading} className="btn-gold shrink-0 rounded-full px-6 py-3.5 text-sm">
            {loading ? "…" : "Track"}
          </button>
        </form>

        {error && (
          <p className="mt-6 text-center text-sm" style={{ color: "var(--mn-terra)" }} data-testid="track-error">{error}</p>
        )}

        {order && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="glass-card mt-10 rounded-[2rem] p-7 sm:p-9"
            data-testid="track-result"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: "var(--mn-text2)" }}>Order</p>
                <p className="font-mono text-xl" style={{ color: "var(--mn-gold)" }} data-testid="track-order-id">{order.order_id}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: "var(--mn-text2)" }}>Arriving by</p>
                <p className="font-serif text-lg" data-testid="track-eta">{fmt(order.tracking.eta)}</p>
              </div>
            </div>

            <div className="mt-8 space-y-0" data-testid="track-timeline">
              {order.tracking.stages.map((s, i) => (
                <div key={s.label} className="relative flex gap-4 pb-7 last:pb-0">
                  {i < order.tracking.stages.length - 1 && (
                    <span
                      className="absolute left-[13px] top-7 h-full w-px"
                      style={{ background: s.done ? "var(--mn-mint)" : "var(--mn-glass-border)" }}
                    />
                  )}
                  <motion.span
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: i * 0.12 }}
                    className="z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border"
                    style={{
                      background: s.done ? "var(--mn-mint)" : "var(--mn-surface)",
                      borderColor: s.done ? "var(--mn-mint)" : "var(--mn-glass-border)",
                      boxShadow: s.current ? "0 0 18px rgba(16,185,129,0.55)" : undefined,
                    }}
                    data-testid={`track-stage-${i}`}
                  >
                    {s.done ? <Check size={13} color="#070D09" strokeWidth={3} /> : <Truck size={12} style={{ color: "var(--mn-text2)" }} />}
                  </motion.span>
                  <div className="pt-0.5">
                    <p className="text-sm font-medium" style={{ color: s.done ? "var(--mn-text)" : "var(--mn-text2)" }}>
                      {s.label}
                      {s.current && (
                        <span className="ml-2 rounded-full px-2 py-0.5 font-mono text-[0.55rem] uppercase tracking-widest" style={{ background: "var(--mn-mint)", color: "#070D09" }}>
                          Now
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 font-mono text-[0.62rem]" style={{ color: "var(--mn-text2)" }}>
                      {s.done ? `Since ${fmt(s.at)}` : `Expected ${fmt(s.at)}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 border-t pt-5" style={{ borderColor: "var(--mn-glass-border)" }}>
              <ul className="space-y-2.5">
                {order.items.map((i) => (
                  <li key={i.slug + i.variant} className="flex items-center gap-3">
                    <img src={i.image} alt="" className="h-11 w-9 rounded-lg object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{i.name}</p>
                      <p className="font-mono text-[0.6rem]" style={{ color: "var(--mn-text2)" }}>{i.variant} × {i.qty}</p>
                    </div>
                    <span className="font-mono text-xs" style={{ color: "var(--mn-gold)" }}>{inr(i.price * i.qty)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center justify-between border-t pt-4" style={{ borderColor: "var(--mn-glass-border)" }}>
                <span className="flex items-center gap-2 text-sm" style={{ color: "var(--mn-text2)" }}>
                  <Package size={14} /> {order.customer.city} · {order.payment_method.toUpperCase()}
                </span>
                <span className="font-serif text-lg" style={{ color: "var(--mn-gold)" }} data-testid="track-total">{inr(order.total)}</span>
              </div>
            </div>
          </motion.div>
        )}

        <p className="mt-10 text-center text-sm" style={{ color: "var(--mn-text2)" }}>
          Need a hand? Call or WhatsApp us on{" "}
          <a href="https://wa.me/919662893341" target="_blank" rel="noopener noreferrer" className="underline-offset-4 hover:underline" style={{ color: "var(--mn-gold)" }} data-testid="track-whatsapp">
            +91 96628 93341
          </a>
        </p>
        <p className="mt-6 text-center">
          <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm transition-colors hover:text-[var(--mn-gold)]" style={{ color: "var(--mn-text2)" }} data-testid="track-continue">
            Continue shopping <ArrowRight size={13} />
          </Link>
        </p>
      </div>
    </div>
  );
}
